/* ALGEA W16 - previous/next arrows for the brand sliders.
   Sliding itself is CSS scroll-snap; the arrows only appear for a mouse
   (see algea-w16.css) and only while the row is wider than the screen. */
(() => {
  var AT = function (s) { return window.AlgeaT ? window.AlgeaT(s) : s; };
  const SELECTOR = '.algea-brand-grid__list, .ag-partners__list';
  const PATHS = { prev: 'M10 3 5 8l5 5', next: 'M6 3l5 5-5 5' };
  const LABELS = { prev: AT('E mëparshmja'), next: AT('Tjetra') };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  // Components below the viewport need no geometry until a customer approaches them.
  function watchVisibility(element, changed) {
    if (!('IntersectionObserver' in window)) { changed(true); return () => {}; }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => changed(entry.isIntersecting));
    }, { rootMargin: '200px 0px' });
    observer.observe(element);
    return () => observer.disconnect();
  }

  // All participating components read geometry before any participant writes styles.
  const measurements = new Map();
  let measurementFrame = 0;
  function scheduleMeasurement(owner, read) {
    measurements.set(owner, read);
    if (measurementFrame) return;
    measurementFrame = requestAnimationFrame(() => {
      measurementFrame = 0;
      const reads = Array.from(measurements.values());
      measurements.clear();
      const writes = reads.map((measure) => measure());
      writes.forEach((write) => { if (write) write(); });
    });
  }
  function cancelMeasurement(owner) {
    measurements.delete(owner);
    if (!measurements.size && measurementFrame) {
      cancelAnimationFrame(measurementFrame);
      measurementFrame = 0;
    }
  }

  function release(root, disconnectedOnly = false) {
    for (const [element, cleanup] of instances) {
      if ((!element.isConnected) || (!disconnectedOnly && (element === root || root.contains(element)))) {
        cleanup();
        instances.delete(element);
      }
    }
  }

  function makeArrow(list, dir, signal) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `ag16-arrow ag16-arrow--${dir}`;
    button.setAttribute('aria-label', LABELS[dir]);
    button.innerHTML = `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="${PATHS[dir]}" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    button.addEventListener('click', () => {
      const step = list.clientWidth * 0.9 * (dir === 'prev' ? -1 : 1);
      list.scrollBy({ left: step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }, { signal });
    return button;
  }

  function setup(list) {
    if (list.dataset.ag16Slider) return;
    list.dataset.ag16Slider = 'true';
    const controller = new AbortController();

    const host = document.createElement('div');
    host.className = 'ag16-slider-host';
    list.parentNode.insertBefore(host, list);
    host.appendChild(list);

    const prev = makeArrow(list, 'prev', controller.signal);
    const next = makeArrow(list, 'next', controller.signal);
    host.append(prev, next);

    let visible = false;
    const update = () => {
      if (!visible) return;
      scheduleMeasurement(list, () => {
        if (!visible || !list.isConnected) return;
        const max = list.scrollWidth - list.clientWidth;
        const left = list.scrollLeft;
        return () => {
          const overflow = String(max > 2);
          if (host.dataset.overflow !== overflow) host.dataset.overflow = overflow;
          if (prev.disabled !== (left <= 2)) prev.disabled = left <= 2;
          if (next.disabled !== (left >= max - 2)) next.disabled = left >= max - 2;
        };
      });
    };
    list.addEventListener('scroll', update, { passive: true, signal: controller.signal });
    const observer = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
    observer?.observe(list);
    const stopVisibility = watchVisibility(host, (inView) => { visible = inView; update(); });
    instances.set(list, () => {
      controller.abort();
      observer?.disconnect();
      stopVisibility();
      cancelMeasurement(list);
      delete list.dataset.ag16Slider;
      prev.remove();
      next.remove();
      // Restore the original list position for a reload of the same section node.
      if (host.parentNode && list.parentNode === host) {
        host.parentNode.insertBefore(list, host);
        host.remove();
      }
    });
    update();
  }

  /* Homepage hero: the arrows are hidden (owner 2026-09-30), so a horizontal swipe
     presses the same hidden next/previous buttons the slideshow already listens to. */
  function setupHeroSwipe(hero) {
    if (hero.dataset.ag16Swipe) return;
    hero.dataset.ag16Swipe = 'true';
    const controller = new AbortController();
    instances.set(hero, () => { controller.abort(); delete hero.dataset.ag16Swipe; });
    let startX = 0;
    let startY = 0;
    hero.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true, signal: controller.signal });
    hero.addEventListener('touchend', (event) => {
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const target = hero.querySelector(dx < 0 ? '[data-hero-next]' : '[data-hero-prev]');
      if (target) target.click();
    }, { passive: true, signal: controller.signal });
  }

  /* Lens anatomy on phones: the treatment cards slide one at a time; the active card's point on
     the lens is highlighted and an arrow runs from it to the card. Tapping a point opens its card. */
  const phone = window.matchMedia('(max-width: 749px)');
  const SVG_NS = 'http://www.w3.org/2000/svg';
  let anatomyId = 0;

  function setupAnatomy(section) {
    if (section.dataset.ag16Anat) return;
    const stage = section.querySelector('.ag-anat__stage');
    const list = section.querySelector('.ag-anat__list');
    const items = Array.from(section.querySelectorAll('.ag-anat__item'));
    const dots = Array.from(section.querySelectorAll('[data-anat-dot]'));
    if (!stage || !list || !items.length || items.length !== dots.length) return;
    section.dataset.ag16Anat = 'true';
    const controller = new AbortController();
    const dotState = dots.map((dot) => ({
      tabindex: dot.getAttribute('tabindex'), current: dot.getAttribute('aria-current'),
    }));

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'ag-anat__mline');
    svg.setAttribute('aria-hidden', 'true');
    const markerId = `ag16-anat-m-${++anatomyId}`;
    svg.innerHTML = `<defs><marker id="${markerId}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" markerUnits="userSpaceOnUse" orient="auto"><path d="M0 0 L10 5 L0 10 z" fill="currentColor"/></marker></defs><path marker-end="url(#${markerId})"/>`;
    stage.appendChild(svg);
    const line = svg.lastChild;
    let active = -1;
    let visible = false;
    let lastPhone = null;

    const activeIndex = () => {
      const step = items[1] ? items[1].offsetLeft - items[0].offsetLeft : list.clientWidth;
      return Math.max(0, Math.min(items.length - 1, Math.round(list.scrollLeft / step)));
    };

    const draw = () => {
      if (!visible || !section.isConnected) return;
      const isPhone = phone.matches;
      let index, box, dot, card, x1, y1, x2, y2;
      if (isPhone) {
        index = activeIndex();
        box = stage.getBoundingClientRect();
        dot = dots[index].getBoundingClientRect();
        card = items[index].getBoundingClientRect();
        x1 = dot.left + dot.width / 2 - box.left;
        y1 = dot.bottom - box.top + 2;
        x2 = Math.min(Math.max(card.left - box.left + 29, 16), box.width - 16);
        y2 = card.top - box.top - 3;
      }
      return () => {
        if (lastPhone !== isPhone) {
          dots.forEach((point) => { point.tabIndex = isPhone ? 0 : -1; });
          lastPhone = isPhone;
        }
        if (!isPhone) { line.removeAttribute('d'); return; }
        if (index !== active) {
          active = index;
          dots.forEach((point, i) => point.setAttribute('aria-current', String(i === index)));
        }
        const viewBox = `0 0 ${box.width} ${box.height}`;
        const shape = `M${x1} ${y1} L${x1} ${y1 + 10} L${x2} ${y2 - 12} L${x2} ${y2}`;
        if (svg.getAttribute('viewBox') !== viewBox) svg.setAttribute('viewBox', viewBox);
        if (line.getAttribute('d') !== shape) line.setAttribute('d', shape);
      };
    };
    const schedule = () => { if (visible) scheduleMeasurement(section, draw); };
    const stopVisibility = watchVisibility(section, (inView) => { visible = inView; schedule(); });
    list.addEventListener('scroll', schedule, { passive: true, signal: controller.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });
    phone.addEventListener('change', schedule, { signal: controller.signal });
    dots.forEach((dot, i) => dot.addEventListener('click', () => {
      if (!phone.matches) return;
      list.scrollTo({ left: items[i].offsetLeft - items[0].offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }, { signal: controller.signal }));
    const img = section.querySelector('.ag-anat__img');
    if (img && !img.complete) img.addEventListener('load', schedule, { once: true, signal: controller.signal });
    instances.set(section, () => {
      controller.abort();
      stopVisibility();
      cancelMeasurement(section);
      svg.remove();
      delete section.dataset.ag16Anat;
      dots.forEach((dot, index) => {
        const state = dotState[index];
        for (const [name, value] of [['tabindex', state.tabindex], ['aria-current', state.current]]) {
          if (value === null) dot.removeAttribute(name);
          else dot.setAttribute(name, value);
        }
      });
    });
    schedule();
  }


  /* W26 (owner 2026-10-06): every shop address in the page body opens Google Maps and every shop
     number opens WhatsApp. Text already inside a link or a control is left alone. */
  const PLACES = [
    { re: /Rrug(?:a|ën|ës) e Barrikadave(?:, Tiranë)?/, href: 'https://maps.app.goo.gl/LWVAgzmrwtfS71KE6' },
    { re: /Rrug(?:a|ën|ës) Gjorg Golemi(?:, Librazhd)?/, href: 'https://maps.app.goo.gl/9BSawCX258vzrdQ76' },
    { re: /(?:\+355|\b0)[\s ]?68[\s ]?806[\s ]?666([12])\b/, href: (m) => `https://wa.me/35568806666${m[1]}` },
  ];
  const SKIP = 'a, button, script, style, textarea, input, select, option, summary, svg, [contenteditable]';
  const ANY = new RegExp(PLACES.map((p) => p.re.source).join('|'));

  function linkifyNode(node) {
    const text = node.nodeValue;
    let best = null;
    PLACES.forEach((place) => {
      const m = place.re.exec(text);
      if (m && (!best || m.index < best.m.index)) best = { m, place };
    });
    if (!best) return;
    const { m, place } = best;
    const link = document.createElement('a');
    link.className = 'ag-autolink';
    link.href = typeof place.href === 'function' ? place.href(m) : place.href;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = m[0];
    const rest = node.splitText(m.index);
    rest.nodeValue = rest.nodeValue.slice(m[0].length);
    node.parentNode.insertBefore(link, rest);
    linkifyNode(rest);
  }

  function linkifyPlaces(root) {
    const scope = root.querySelectorAll ? root.querySelectorAll('main, footer') : [];
    const insideContent = root.closest && root.closest('main, footer');
    const roots = insideContent || (root.matches && root.matches('main, footer')) ? [root] : Array.from(scope);
    roots.forEach((container) => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => (ANY.test(n.nodeValue) && !n.parentElement.closest(SKIP) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(linkifyNode);
    });
  }

  const init = (root) => {
    release(root, true);
    root.querySelectorAll(SELECTOR).forEach(setup);
    root.querySelectorAll('[data-hero-slideshow]').forEach(setupHeroSwipe);
    root.querySelectorAll('[data-anat]').forEach(setupAnatomy);
    linkifyPlaces(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => release(event.target));
})();
